/**
 * Pointer-follow movement — hold the pointer down, the ship steers toward the world point under
 * it; release and it stops.
 *
 * This is a third movement scheme alongside AMD-1's virtual stick and §3.1's WASD/left-stick. It
 * produces a **wish direction and magnitude** exactly like a stick does and nothing else: §3.2's
 * acceleration model (8 u/s top speed, 64 accel, 96 brake, 128 reverse) is untouched, so a follow
 * pointer can never teleport, snap or bypass the curve.
 *
 * Two rules make it feel like a place rather than a joystick:
 *
 * 1. **The target is a world position, resolved once per pointer event.** A screen point re-read
 *    every frame is a stick with extra steps: the camera follows the player, so a stationary finger
 *    keeps the same screen *offset* and the ship chases the screen centre forever. Resolving the
 *    world point when the pointer moves and holding it still otherwise is what lets the ship
 *    actually arrive.
 * 2. **A stop radius.** Inside it the wish magnitude is zero, so the ship settles instead of
 *    hunting back and forth across the point.
 */

import type { Vec2 } from '../sim/entities';
import { applyRadialDeadzone } from './gamepad';

/** Which movement scheme the touch surface runs. `stick` is AMD-1 verbatim and is the default. */
export type MovementScheme = 'stick' | 'follow';

/**
 * Pointer distance at which the wish magnitude saturates, CSS px.
 *
 * 120 px is 3× AMD-1's 44 px stick travel radius (and 3× the §13.5 Fitts' floor), so a thumb that
 * would have saturated the stick saturates the follow at the same *gesture* size scaled to a whole
 * screen. On the 390×844 portrait reference it is 31% of the viewport width, so a finger parked
 * anywhere past a third of the screen from the ship runs at top speed and the taper only bites over
 * the last stretch — which is the arrival behaviour the scheme exists for. Through the §3.5 remap
 * (inner 0.18, outer 0.92) the walk band runs 22 px → 110 px.
 */
export const FOLLOW_FULL_SPEED_PX = 120;

/**
 * Inside this distance the wish is zero, CSS px.
 *
 * 28 px is chosen against the brake, not against the art. At the 390×844 framing the camera shows
 * 20 world units across 390 px, so 28 px ≈ 1.44 u, while §3.2's brake stops the ship from top
 * speed in v²/2B = 8²/192 = 0.33 u ≈ 6.5 px. A ship crossing the boundary at full speed therefore
 * halts about a fifth of the way in and cannot reach the far side to re-trigger — settling is
 * structural, not damped. It is also larger than the 12 px tap slop (a tap never leaves a crawl)
 * and than the 22 px the dead zone alone would give, so the stop radius, not the dead zone, is the
 * binding constraint.
 */
export const FOLLOW_STOP_RADIUS_PX = 28;

/** Baseline used to measure world units per CSS px. Long enough that float error is irrelevant. */
export const FOLLOW_SCALE_PROBE_PX = 100;

/**
 * Read-only follow state for the §13 HUD (R8 may draw a marker at the target).
 * Screen coordinates are CSS px relative to the game surface's top-left; world coordinates are
 * sim units with y up.
 */
export interface FollowView {
  active: boolean;
  x: number;
  y: number;
  worldX: number;
  worldY: number;
  /** Post-dead-zone magnitude, 0..1. Zero inside the stop radius. */
  magnitude: number;
  /** Unit wish direction in world space. Zero when the magnitude is zero. */
  dirX: number;
  dirY: number;
}

export function makeFollowView(): FollowView {
  return { active: false, x: 0, y: 0, worldX: 0, worldY: 0, magnitude: 0, dirX: 0, dirY: 0 };
}

/**
 * Player → target delta (world units) to a §3.2 wish vector, written into `out`. Returns the
 * magnitude, 0..1.
 *
 * The distance is normalised by `fullDist` and then run through the *same* §3.5 radial dead-zone
 * remap a stick uses — inner 0.18, outer 0.92, renormalised, direction preserved, linear — so one
 * curve serves stick, pad and pointer. `stopDist` is applied first and overrides everything.
 */
export function followWish(
  dx: number,
  dy: number,
  fullDist: number,
  stopDist: number,
  inner: number,
  outer: number,
  out: Vec2,
): number {
  const d = Math.sqrt(dx * dx + dy * dy);
  if (d <= stopDist || d <= 0 || fullDist <= 0) {
    out.x = 0;
    out.y = 0;
    return 0;
  }
  return applyRadialDeadzone(dx / fullDist, dy / fullDist, inner, outer, out);
}

/**
 * World units per CSS px, measured off the caller's own screen→world mapping so the input layer
 * never learns the projection. `a` and `b` are scratch; this allocates nothing.
 */
export function pointerScale(
  cameraToWorld: (sx: number, sy: number, out: Vec2) => void,
  a: Vec2,
  b: Vec2,
): number {
  cameraToWorld(0, 0, a);
  cameraToWorld(FOLLOW_SCALE_PROBE_PX, 0, b);
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const s = Math.sqrt(dx * dx + dy * dy) / FOLLOW_SCALE_PROBE_PX;
  return s > 1e-9 ? s : 1;
}

/**
 * One held pointer driving the follow. Tracked by `pointerId`, never by index (§ multi-touch:
 * a second finger is a different role, and a finger that lifts must not hand its identity to the
 * next one down).
 *
 * `dirty` is the whole world-anchor rule in one flag: it is raised when the pointer lands or moves
 * and lowered when the owner resolves the screen point to a world point. A pointer that has not
 * moved keeps the world point it was given.
 */
export class FollowPointer {
  pointerId = -1;
  readonly view: FollowView = makeFollowView();
  dirty = false;
  downTick = -1;
  /** Peak straight-line travel from the touch-down point, CSS px — the §3.6 tap-slop measure. */
  travel = 0;
  /** Touch-down point, CSS px. The double-tap gate pairs contacts by where they landed. */
  downX = 0;
  downY = 0;

  begin(id: number, x: number, y: number, tick: number): void {
    this.pointerId = id;
    this.view.active = true;
    this.view.x = x;
    this.view.y = y;
    this.view.magnitude = 0;
    this.view.dirX = 0;
    this.view.dirY = 0;
    this.dirty = true;
    this.downTick = tick;
    this.downX = x;
    this.downY = y;
    this.travel = 0;
  }

  move(x: number, y: number): void {
    if (x === this.view.x && y === this.view.y) return;
    this.view.x = x;
    this.view.y = y;
    this.dirty = true;
    const dx = x - this.downX;
    const dy = y - this.downY;
    const d = Math.sqrt(dx * dx + dy * dy);
    if (d > this.travel) this.travel = d;
  }

  end(): void {
    this.pointerId = -1;
    this.view.active = false;
    this.view.magnitude = 0;
    this.view.dirX = 0;
    this.view.dirY = 0;
    this.dirty = false;
    this.downTick = -1;
  }
}
